import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { BoneIcon as Bone, PawPrintIcon as PawPrint, DropIcon as Drop, ConfettiIcon as Confetti, LightningIcon as Lightning, HeartIcon as Heart, PhoneIcon as Phone } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { formatApiError, thumbUrl } from "@/lib/utils-app";

const KIND_META = {
  meal: { label: "Fed", icon: Bone, color: "#C9A227" },
  walk: { label: "Walk", icon: PawPrint, color: "#3F4A8A" },
  potty: { label: "Potty", icon: Drop, color: "#0F8043" },
  play: { label: "Play", icon: Confetti, color: "#C1272D" },
  energy: { label: "Energy", icon: Lightning, color: "#8A6A00" },
};

/** Public sitter view — token-based, no login required.
 *  Sitters open this URL on their phone during the stay, see the dog's
 *  routine + emergency contacts, and tap to log activities live. */
export default function SitterView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [walkOpen, setWalkOpen] = useState(false);
  const [energyOpen, setEnergyOpen] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      // Use axios directly because the public endpoint doesn't require
      // (and we don't want to send) the user's JWT — bypass the api
      // wrapper's auth header.
      const r = await fetch(`${API}/care/sitter/${token}`);
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || "Unable to load sitter view.");
      }
      setData(await r.json());
    } catch (e) {
      setError(e.message);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const log = async (kind, extras = {}) => {
    setBusy(kind);
    try {
      const r = await fetch(`${API}/care/sitter/${token}/log`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ kind, ...extras }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || "Unable to log");
      }
      toast.success(`${KIND_META[kind].label} logged`);
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-6">
        <div className="max-w-md bg-white rounded-2xl border border-[#EAEAEA] p-6 text-center" data-testid="sitter-view-error">
          <h1 className="font-spectral text-2xl mb-2">Link not available</h1>
          <p className="text-sm text-[#6B6B6B]">{error}</p>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen flex items-center justify-center text-[#6B6B6B]">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-12" data-testid="sitter-view">
      {/* Top bar */}
      <header className="bg-white border-b border-[#EAEAEA] px-4 py-3 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          {data.dog.photos?.[0] ? (
            <img src={thumbUrl(data.dog.photos[0])} alt={data.dog.name} className="w-10 h-10 rounded-full object-cover" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-cream border border-[#E2DAC2] flex items-center justify-center">
              <PawPrint size={18} weight="duotone" className="text-gold" />
            </div>
          )}
          <div>
            <p className="font-spectral text-lg leading-tight">{data.dog.name}</p>
            <p className="text-[11px] text-[#6B6B6B]">{data.dog.breed} · sitter view</p>
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 pt-5 space-y-5">
        {/* Quick info */}
        <section className="bg-white rounded-xl border border-[#EAEAEA] p-5 space-y-3" data-testid="sitter-info">
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-gold flex items-center gap-1.5">
            <Heart size={11} weight="fill" /> Owner: {data.owner_name}
          </p>
          <p className="text-[11px] text-[#6B6B6B]">Active sitter window: <strong>{data.starts_on} → {data.ends_on}</strong></p>
          {data.feeding_instructions && (
            <div>
              <p className="text-[11px] uppercase tracking-[0.15em] font-bold text-[#6B6B6B] mb-0.5">Feeding</p>
              <p className="text-sm whitespace-pre-wrap">{data.feeding_instructions}</p>
            </div>
          )}
          {data.meds && (
            <div>
              <p className="text-[11px] uppercase tracking-[0.15em] font-bold text-[#6B6B6B] mb-0.5">Medications</p>
              <p className="text-sm whitespace-pre-wrap">{data.meds}</p>
            </div>
          )}
          {data.owner_notes && (
            <div>
              <p className="text-[11px] uppercase tracking-[0.15em] font-bold text-[#6B6B6B] mb-0.5">Notes</p>
              <p className="text-sm whitespace-pre-wrap">{data.owner_notes}</p>
            </div>
          )}
          {(data.vet_phone || data.emergency_contact) && (
            <div className="pt-2 mt-2 border-t border-[#EAEAEA] grid grid-cols-1 sm:grid-cols-2 gap-2">
              {data.vet_phone && (
                <a href={`tel:${data.vet_phone}`} className="flex items-center gap-2 text-sm font-semibold text-crimson hover:underline" data-testid="sitter-vet-phone">
                  <Phone size={14} weight="duotone" /> Vet: {data.vet_phone}
                </a>
              )}
              {data.emergency_contact && (
                <a href={`tel:${data.emergency_contact.replace(/[^\d+]/g, "")}`} className="flex items-center gap-2 text-sm font-semibold text-crimson hover:underline" data-testid="sitter-emergency">
                  <Phone size={14} weight="duotone" /> Emergency: {data.emergency_contact}
                </a>
              )}
            </div>
          )}
        </section>

        {/* Log buttons */}
        <section className="bg-white rounded-xl border border-[#EAEAEA] p-5">
          <h2 className="font-spectral text-xl mb-3">Log activity</h2>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="sitter-log-buttons">
            {Object.entries(KIND_META).map(([kind, meta]) => {
              const Icon = meta.icon;
              const onClick =
                kind === "walk" ? () => setWalkOpen(true) :
                kind === "energy" ? () => setEnergyOpen(true) :
                () => log(kind);
              return (
                <button
                  key={kind}
                  type="button"
                  onClick={onClick}
                  disabled={busy === kind}
                  data-testid={`sitter-log-${kind}`}
                  className="flex flex-col items-center gap-1.5 p-4 rounded-xl border border-[#EAEAEA] hover:border-ink transition-colors disabled:opacity-50"
                >
                  <Icon size={28} weight="duotone" style={{ color: meta.color }} />
                  <span className="text-sm font-semibold">{meta.label}</span>
                </button>
              );
            })}
          </div>
        </section>

        {/* Timeline */}
        <section className="bg-white rounded-xl border border-[#EAEAEA] p-5">
          <h2 className="font-spectral text-xl mb-3">Recent activity</h2>
          {data.log.length === 0 ? (
            <p className="text-sm text-[#6B6B6B]">Nothing logged yet. Tap any button above to start.</p>
          ) : (
            <div className="space-y-1.5">
              {data.log.slice(0, 30).map((e) => {
                const meta = KIND_META[e.kind] || KIND_META.meal;
                const Icon = meta.icon;
                const time = (e.occurred_at || "").slice(11, 16);
                const bits = [];
                if (e.distance_mi) bits.push(`${e.distance_mi} mi`);
                if (e.duration_min) bits.push(`${e.duration_min} min`);
                if (e.energy_level) bits.push(`energy ${e.energy_level}/5`);
                if (e.notes) bits.push(e.notes);
                return (
                  <div key={e.id} className="flex items-center gap-3 text-sm" data-testid={`sitter-entry-${e.id}`}>
                    <span className="text-[10px] font-mono text-[#9A9A9A] w-12 flex-shrink-0">{time}</span>
                    <Icon size={14} weight="duotone" style={{ color: meta.color }} className="flex-shrink-0" />
                    <span className="font-medium w-16 flex-shrink-0">{meta.label}</span>
                    <span className="text-[#6B6B6B] flex-1 truncate">{bits.join(" · ") || "—"}</span>
                    {e.logged_by === "sitter" && (
                      <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-gold bg-gold/10 rounded-full px-1.5 py-0.5">you</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <p className="text-[11px] text-[#9A9A9A] text-center pt-2">
          Powered by Armstrong K9 Registry · Dog Log
        </p>
      </div>

      {/* Walk modal */}
      <SitterWalkDialog open={walkOpen} onOpenChange={setWalkOpen} onSave={(extras) => log("walk", extras)} />
      <SitterEnergyDialog open={energyOpen} onOpenChange={setEnergyOpen} onSave={(extras) => log("energy", extras)} />
    </div>
  );
}

function SitterWalkDialog({ open, onOpenChange, onSave }) {
  const [distance, setDistance] = useState("");
  const [duration, setDuration] = useState("");
  const [notes, setNotes] = useState("");
  const submit = async () => {
    await onSave({
      distance_mi: distance ? parseFloat(distance) : null,
      duration_min: duration ? parseInt(duration, 10) : null,
      notes: notes || null,
    });
    setDistance(""); setDuration(""); setNotes("");
    onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="sitter-walk-dialog">
        <DialogHeader>
          <DialogTitle>Log a walk</DialogTitle>
          <DialogDescription>All fields optional.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Distance (mi)</Label>
            <Input type="number" step="0.1" value={distance} onChange={(e) => setDistance(e.target.value)} />
          </div>
          <div>
            <Label>Duration (min)</Label>
            <Input type="number" value={duration} onChange={(e) => setDuration(e.target.value)} />
          </div>
        </div>
        <div>
          <Label>Notes</Label>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="route, mood, ..." />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
          <Button onClick={submit} className="rounded-full bg-ink hover:bg-crimson text-white">Save walk</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SitterEnergyDialog({ open, onOpenChange, onSave }) {
  const [level, setLevel] = useState(3);
  const [notes, setNotes] = useState("");
  const submit = async () => {
    await onSave({ energy_level: level, notes: notes || null });
    setLevel(3); setNotes("");
    onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="sitter-energy-dialog">
        <DialogHeader>
          <DialogTitle>Energy level</DialogTitle>
          <DialogDescription>1 = sleepy, 5 = bouncing off the walls.</DialogDescription>
        </DialogHeader>
        <div className="flex justify-between gap-2">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setLevel(n)}
              className={`h-12 w-12 rounded-full text-lg font-bold transition-colors ${
                level === n ? "bg-ink text-white" : "bg-[#FAFAFA] border border-[#EAEAEA] hover:border-ink"
              }`}
            >{n}</button>
          ))}
        </div>
        <div>
          <Label>Notes</Label>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
          <Button onClick={submit} className="rounded-full bg-ink hover:bg-crimson text-white">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
